import { keepPreviousData, useQuery } from "@tanstack/react-query";
import { Download, History, RefreshCw } from "lucide-react";
import { useEffect, useMemo, useState } from "react";

import { DescargandoOverlay } from "@/components/DescargandoOverlay";
import {
  downloadDemandaRetirosCsv,
  getDemandaRetiros,
  getDemandaRetirosCcrs,
  getDemandaRetirosResumen,
  type DemandaRetirosVista,
} from "@/lib/api";

const PAGE_SIZE = 100;

// Último corte entregado (por navegador/usuario). Guarda la última fecha de
// publicación que cubrió la descarga, para proponerla como "publicado desde".
const CORTE_KEY = "demanda-retiros:ultimo-corte";

type Corte = { fecha_publicacion: string; descargado_en: string; vista: DemandaRetirosVista };

function leerCorte(): Corte | null {
  try {
    const raw = localStorage.getItem(CORTE_KEY);
    return raw ? (JSON.parse(raw) as Corte) : null;
  } catch {
    return null;
  }
}

function guardarCorte(c: Corte): void {
  try {
    localStorage.setItem(CORTE_KEY, JSON.stringify(c));
  } catch {
    /* almacenamiento no disponible */
  }
}

const VISTAS: { key: DemandaRetirosVista; label: string }[] = [
  { key: "agregado", label: "Por centro de control" },
  { key: "detalle", label: "Detalle de retiros" },
];

const LABELS: Record<string, string> = {
  centro_control_regional: "Centro de control",
  sistema: "Sistema",
  zona_carga: "Zona de carga",
  fecha_operacion: "Fecha operación",
  hora: "Hora",
  liquidacion: "Liq.",
  version: "Ver.",
  demanda_mwh: "Demanda (MWh)",
  fecha_publicacion: "Publicación",
  actualizado_en: "Cargado",
};

const NUM_RIGHT = new Set(["hora", "liquidacion", "version", "demanda_mwh"]);
const DEC = new Intl.NumberFormat("es-MX", {
  minimumFractionDigits: 2,
  maximumFractionDigits: 2,
});
const INT = new Intl.NumberFormat("es-MX");

function fmtCell(col: string, v: string | number | null): string {
  if (v == null || v === "") return "—";
  if (col === "demanda_mwh") {
    const n = typeof v === "number" ? v : Number(v);
    return Number.isFinite(n) ? DEC.format(n) : String(v);
  }
  if (col === "actualizado_en" && typeof v === "string") {
    return v.replace("T", " ").slice(0, 19);
  }
  return String(v);
}

function fmtFecha(iso: string | null | undefined): string {
  if (!iso) return "—";
  const [y, m, d] = iso.slice(0, 10).split("-");
  return `${d}/${m}/${y}`;
}

export function DemandaRetirosPage() {
  const [corte, setCorte] = useState<Corte | null>(() => leerCorte());
  const [vista, setVista] = useState<DemandaRetirosVista>("detalle");
  // Corte incremental: publicaciones de CENACE a partir de esta fecha.
  const [publicadoDesde, setPublicadoDesde] = useState<string>(
    () => leerCorte()?.fecha_publicacion ?? "",
  );
  const [desde, setDesde] = useState("");
  const [hasta, setHasta] = useState("");
  const [ccr, setCcr] = useState("");
  const [page, setPage] = useState(0);
  const [descargando, setDescargando] = useState(false);
  const [errMsg, setErrMsg] = useState<string | null>(null);
  const [okMsg, setOkMsg] = useState<string | null>(null);

  const ccrsQ = useQuery({
    queryKey: ["demanda-retiros:ccrs"],
    queryFn: getDemandaRetirosCcrs,
    staleTime: 60 * 60 * 1000,
  });

  const resumenQ = useQuery({
    queryKey: ["demanda-retiros:resumen", publicadoDesde],
    queryFn: () => getDemandaRetirosResumen(publicadoDesde || undefined),
    placeholderData: keepPreviousData,
    staleTime: 5 * 60 * 1000,
  });

  useEffect(() => {
    setPage(0);
  }, [vista, publicadoDesde, desde, hasta, ccr]);

  const filters = useMemo(
    () => ({
      vista,
      publicado_desde: publicadoDesde || undefined,
      desde: desde || undefined,
      hasta: hasta || undefined,
      ccr: ccr || undefined,
      limit: PAGE_SIZE,
      offset: page * PAGE_SIZE,
    }),
    [vista, publicadoDesde, desde, hasta, ccr, page],
  );

  const q = useQuery({
    queryKey: ["demanda-retiros", filters],
    queryFn: () => getDemandaRetiros(filters),
    placeholderData: keepPreviousData,
  });

  const columns = q.data?.columns ?? [];
  const items = q.data?.items ?? [];
  const hasMore = q.data?.has_more ?? false;
  const resumen = resumenQ.data;
  const nuevo = resumen?.nuevo ?? null;
  const puedeDescargar = Boolean(publicadoDesde || desde);

  const onDescargar = async () => {
    if (!puedeDescargar || descargando) return;
    setDescargando(true);
    setErrMsg(null);
    setOkMsg(null);
    try {
      await downloadDemandaRetirosCsv({
        vista,
        publicado_desde: publicadoDesde || undefined,
        desde: desde || undefined,
        hasta: hasta || undefined,
        ccr: ccr || undefined,
      });
      // El corte entregado = última publicación cargada al momento de descargar.
      // Se guarda inclusive (>=) para que la próxima entrega no deje huecos si
      // el ETL cargó más filas de esa misma fecha después de la descarga.
      const cubierto = resumen?.max_fecha_publicacion;
      if (cubierto) {
        const nuevoCorte: Corte = {
          fecha_publicacion: cubierto,
          descargado_en: new Date().toISOString(),
          vista,
        };
        guardarCorte(nuevoCorte);
        setCorte(nuevoCorte);
        setOkMsg(
          `CSV generado. Corte guardado: publicaciones hasta el ${fmtFecha(cubierto)}. ` +
            `La próxima entrega usa esa fecha como "publicado desde".`,
        );
      } else {
        setOkMsg("CSV generado.");
      }
    } catch (e) {
      setErrMsg((e as Error).message);
    } finally {
      setDescargando(false);
    }
  };

  const usarUltimoCorte = () => {
    if (corte) setPublicadoDesde(corte.fecha_publicacion);
  };

  return (
    <div className="space-y-4">
      <DescargandoOverlay
        visible={descargando}
        detalle="Estamos generando el CSV con los retiros, espera un momento."
      />

      <div className="bg-white border rounded-lg p-4">
        <div className="flex items-start justify-between flex-wrap gap-3">
          <div>
            <h2 className="text-base font-semibold text-slate-800">
              Retiros por centro de control (SIN) — entrega incremental
            </h2>
            <p className="text-xs text-slate-500 mt-0.5 max-w-3xl">
              Demanda real de retiros del SIN mapeada a su centro de control regional
              (una zona de carga → un centro; sin inflar valores). El corte
              incremental es la <b>fecha de publicación</b> de CENACE: "publicado
              desde X" trae los días nuevos y también las reliquidaciones de días
              anteriores publicadas a partir de esa fecha.
            </p>
          </div>
          <div className="flex flex-col items-end gap-1">
            <button
              type="button"
              onClick={onDescargar}
              disabled={descargando || !puedeDescargar}
              title={
                puedeDescargar
                  ? "Descargar CSV con los filtros actuales"
                  : "Indica un corte 'publicado desde' o una fecha de operación 'desde'"
              }
              className="inline-flex items-center gap-2 px-3 py-1.5 text-sm rounded bg-blue-600 text-white hover:bg-blue-700 disabled:opacity-50"
            >
              <Download className="w-4 h-4" />
              {descargando ? "Generando CSV…" : "Descargar CSV"}
            </button>
            {!puedeDescargar && (
              <span className="text-[11px] text-amber-700">
                Elige un corte o una fecha "desde" para poder descargar.
              </span>
            )}
          </div>
        </div>

        {/* Estado de la fuente */}
        <div className="mt-3 flex flex-wrap gap-x-6 gap-y-1 text-xs text-slate-600 border-t pt-3">
          <span>
            Última publicación cargada:{" "}
            <b className="text-slate-800">{fmtFecha(resumen?.max_fecha_publicacion)}</b>
          </span>
          <span>
            Última fecha de operación:{" "}
            <b className="text-slate-800">{fmtFecha(resumen?.max_fecha_operacion)}</b>
          </span>
          {corte && (
            <span className="inline-flex items-center gap-1">
              <History className="w-3.5 h-3.5" />
              Tu última entrega cubrió publicaciones hasta el{" "}
              <b className="text-slate-800">{fmtFecha(corte.fecha_publicacion)}</b>
              <span className="text-slate-400">
                ({corte.descargado_en.slice(0, 10)})
              </span>
              {publicadoDesde !== corte.fecha_publicacion && (
                <button
                  type="button"
                  onClick={usarUltimoCorte}
                  className="ml-1 text-blue-600 hover:underline"
                >
                  usar ese corte
                </button>
              )}
            </span>
          )}
          {resumenQ.isFetching && (
            <span className="text-slate-400 inline-flex items-center gap-1">
              <RefreshCw className="w-3 h-3 animate-spin" /> actualizando…
            </span>
          )}
        </div>

        {/* Filtros */}
        <div className="flex items-center gap-3 flex-wrap mt-3">
          <div className="inline-flex border rounded overflow-hidden">
            {VISTAS.map((v) => (
              <button
                key={v.key}
                type="button"
                onClick={() => setVista(v.key)}
                className={`px-3 py-1.5 text-sm border-l first:border-l-0 ${
                  vista === v.key
                    ? "bg-blue-600 text-white"
                    : "bg-white hover:bg-slate-50 text-slate-700"
                }`}
              >
                {v.label}
              </button>
            ))}
          </div>

          <label className="text-sm text-slate-700 inline-flex items-center gap-1.5 font-medium">
            Publicado desde
            <input
              type="date"
              value={publicadoDesde}
              onChange={(e) => setPublicadoDesde(e.target.value)}
              className="border border-blue-300 rounded px-2 py-1 text-sm font-normal"
            />
            {publicadoDesde && (
              <button
                type="button"
                onClick={() => setPublicadoDesde("")}
                className="text-xs text-slate-500 hover:text-slate-800"
                title="Quitar el corte de publicación"
              >
                quitar
              </button>
            )}
          </label>

          <span className="text-slate-300">|</span>

          <label className="text-sm text-slate-600 inline-flex items-center gap-1.5">
            Fecha operación desde
            <input
              type="date"
              value={desde}
              onChange={(e) => setDesde(e.target.value)}
              className="border rounded px-2 py-1 text-sm"
            />
          </label>
          <label className="text-sm text-slate-600 inline-flex items-center gap-1.5">
            hasta
            <input
              type="date"
              value={hasta}
              onChange={(e) => setHasta(e.target.value)}
              className="border rounded px-2 py-1 text-sm"
            />
          </label>
          <label className="text-sm text-slate-600 inline-flex items-center gap-1.5">
            Centro de control
            <select
              value={ccr}
              onChange={(e) => setCcr(e.target.value)}
              className="border rounded px-2 py-1 text-sm"
            >
              <option value="">Todos</option>
              {(ccrsQ.data?.items ?? []).map((c) => (
                <option key={c} value={c}>
                  {c}
                </option>
              ))}
            </select>
          </label>

          {q.isFetching && (
            <span className="text-[11px] text-slate-400 inline-flex items-center gap-1">
              <RefreshCw className="w-3 h-3 animate-spin" /> cargando…
            </span>
          )}
        </div>

        {/* Qué hay de nuevo desde el corte */}
        {publicadoDesde && nuevo && (
          <div className="mt-3 text-sm rounded border border-blue-100 bg-blue-50 px-3 py-2 text-blue-900">
            {nuevo.filas === 0 ? (
              <>
                No hay publicaciones nuevas desde el <b>{fmtFecha(publicadoDesde)}</b>.
              </>
            ) : (
              <>
                Publicado desde el <b>{fmtFecha(publicadoDesde)}</b>:{" "}
                <b>{INT.format(nuevo.filas)}</b> filas de retiros en{" "}
                <b>{INT.format(nuevo.dias_operacion)}</b> días de operación (del{" "}
                {fmtFecha(nuevo.op_desde)} al {fmtFecha(nuevo.op_hasta)}).
                {ccr && <span className="text-blue-700"> El conteo es de todo el SIN, sin filtrar por centro.</span>}
              </>
            )}
          </div>
        )}
      </div>

      {okMsg && (
        <div className="bg-emerald-50 border border-emerald-200 rounded-lg p-3 text-sm text-emerald-800">
          {okMsg}
        </div>
      )}
      {errMsg && (
        <div className="bg-red-50 border border-red-200 rounded-lg p-3 text-sm text-red-700">
          {errMsg}
        </div>
      )}

      {q.isError && (
        <div className="bg-white border rounded-lg p-8 text-center text-red-600 text-sm">
          Error: {(q.error as Error).message}
        </div>
      )}

      {!q.isError && (
        <div className="bg-white border rounded-lg overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full text-sm whitespace-nowrap">
              <thead className="bg-slate-100 text-slate-600 text-left">
                <tr>
                  {columns.map((c) => (
                    <th
                      key={c}
                      className={`px-3 py-2 font-medium ${NUM_RIGHT.has(c) ? "text-right" : ""}`}
                    >
                      {LABELS[c] ?? c}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {items.map((row, i) => (
                  <tr key={i} className="border-t hover:bg-slate-50">
                    {columns.map((c) => (
                      <td
                        key={c}
                        className={`px-3 py-1.5 ${
                          NUM_RIGHT.has(c) ? "text-right tabular-nums" : ""
                        } ${c === "demanda_mwh" ? "font-medium" : ""}`}
                      >
                        {fmtCell(c, row[c])}
                      </td>
                    ))}
                  </tr>
                ))}
                {items.length === 0 && !q.isFetching && (
                  <tr>
                    <td
                      colSpan={Math.max(1, columns.length)}
                      className="px-3 py-8 text-center text-slate-500"
                    >
                      Sin datos para los filtros seleccionados.
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>

          <div className="px-4 py-3 border-t flex items-center justify-between text-sm">
            <div className="text-slate-500">
              Página {page + 1}
              {items.length > 0 && (
                <span className="text-slate-400">
                  {" "}
                  · filas {page * PAGE_SIZE + 1}–{page * PAGE_SIZE + items.length}
                </span>
              )}
            </div>
            <div className="flex gap-2">
              <button
                type="button"
                className="border rounded px-3 py-1 disabled:opacity-50"
                onClick={() => setPage((p) => Math.max(0, p - 1))}
                disabled={page === 0 || q.isFetching}
              >
                ← Anterior
              </button>
              <button
                type="button"
                className="border rounded px-3 py-1 disabled:opacity-50"
                onClick={() => setPage((p) => p + 1)}
                disabled={!hasMore || q.isFetching}
              >
                Siguiente →
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
